/**
 * Normalize file object to ensure it has required properties
 * @param {File|Object} file - File or file-like object
 * @returns {File|Object} Normalized file object with guaranteed name property
 */
function normalizeFileObject(file) {
	if (!file) return null;

	// CRITICAL: ALWAYS preserve File/Blob instances - NEVER spread them!
	// Spreading destroys File prototype methods (.text(), .arrayBuffer(), etc.)
	// and loses the actual file content!
	if (file instanceof File || file instanceof Blob) {
		// File/Blob objects must be returned as-is, even if name is missing
		if (
			!file.name ||
			typeof file.name !== "string" ||
			file.name.trim() === ""
		) {
			console.warn(
				"File/Blob instance has problematic name, but preserving File instance:",
				{
					fileName: file.name,
					fileType: file.type,
					fileSize: file.size,
					constructor: file.constructor?.name,
				},
			);
		}
		return file; // Return original File/Blob unchanged
	}

	// If we reach here, it's NOT a File/Blob instance
	// Could be a test mock or incorrectly constructed object
	console.error(
		"❌ CRITICAL: Received non-File/Blob object in validation module:",
		{
			type: typeof file,
			constructor: file?.constructor?.name,
			hasName: !!file?.name,
			isFileList: file instanceof FileList,
		},
	);

	// For non-File objects with a proper name, assume it's a mock/test object
	if (file?.name && typeof file.name === "string" && file.name.trim() !== "") {
		console.warn(
			"Non-File object with name property detected (test mock?), allowing through",
		);
		return file;
	}

	// Otherwise, this is an invalid file object - FAIL
	throw new Error(
		"Invalid file object: Expected File or Blob instance, got " +
			(file?.constructor?.name || typeof file) +
			". This indicates a system error in file handling.",
	);
}

// Core validation module logic
async function validationModuleCore(
	fileInput,
	validationFunction,
	config = {},
) {
	try {
		// Extract file from array/FileList if needed
		let rawFile;
		if (Array.isArray(fileInput)) {
			rawFile = fileInput[0];
		} else if (fileInput instanceof FileList) {
			rawFile = fileInput[0];
		} else {
			rawFile = fileInput;
		}

		// Debug log only when there might be issues
		if (!rawFile?.name) {
			console.warn(
				"validationModuleCore: File extraction resulted in missing name:",
				{
					inputType: typeof fileInput,
					isArray: Array.isArray(fileInput),
					isFileList: fileInput instanceof FileList,
					length: fileInput?.length,
					extractedFileName: rawFile?.name,
					constructor: fileInput?.constructor?.name,
				},
			);
		}

		const file = normalizeFileObject(rawFile);

		// Basic file validation
		if (!file) {
			throw new Error("No file provided");
		}

		// File size validation (default 5MB)
		const maxSize = config.maxSize || 5 * 1024 * 1024;
		if (typeof file.size === "number" && file.size > maxSize) {
			throw new Error(
				`File size exceeds ${Math.ceil(maxSize / 1024 / 1024)}MB limit`,
			);
		} else if (file.size === undefined || file.size === null) {
			console.warn(
				"File size is missing in validationModule, skipping size validation",
			);
		}

		// File type validation (if specified)
		if (config.accept && config.accept !== "*") {
			const acceptedTypes = config.accept.split(",").map((type) => type.trim());
			const fileName = file.name || "unknown_file";
			const fileExtension = fileName.includes(".")
				? `.${fileName.split(".").pop().toLowerCase()}`
				: "";
			const mimeType = file.type;

			const isValidExtension = acceptedTypes.some(
				(type) =>
					type === fileExtension || fileName.toLowerCase().endsWith(type),
			);
			const isValidMimeType = acceptedTypes.some(
				(type) => mimeType === type || mimeType.includes(type.replace(".", "")),
			);

			if (!isValidExtension && !isValidMimeType) {
				throw new Error(`Please select a valid file type: ${config.accept}`);
			}
		}

		// Custom validation
		if (validationFunction && typeof validationFunction === "function") {
			// Check if it's a WASM validation (ACH files)
			if (validationFunction.name === "parseACH" || config.isWASM) {
				// WASM validation for ACH files
				await window.wasmReady;
				const content = await file.text();

				if (typeof window.parseACH === "function") {
					const result = window.parseACH(content);
					if (typeof result !== "string" || result[0] !== "{") {
						throw new Error("Invalid ACH file format");
					}
					// Return parsed content for ACH files
					return { validated: true, content: result };
				} else {
					throw new Error("ACH validator not loaded");
				}
			} else {
				// Check if this is a CSV file - redirect to proper validation system
				const fileName = file.name || "unknown_file";
				if (
					file.type === "text/csv" ||
					fileName.toLowerCase().endsWith(".csv")
				) {
					console.warn(
						"🚨 CSV file detected in validationModule - redirecting to proper validation system",
					);
					console.warn(
						"⚠️ CSV files must be processed through ValidationRulesService.validateWithRules to ensure formatCsvFile API is used",
					);

					// CSV files should not be processed here - they must go through
					// ValidationRulesService.validateWithRules to ensure formatCsvFile API is used
					throw new Error(
						"CSV files must be validated through ValidationRulesService with proper formatCsvFile API preprocessing. Use $validateWithRulesFirst instead.",
					);
				} else {
					// Callback validation for other files
					const isValid = await validationFunction(
						file,
						config.validationConfig || {},
					);
					if (!isValid) {
						throw new Error("File validation failed");
					}
				}
			}
		}

		// Return validation success
		return { validated: true, content: file };
	} catch (error) {
		console.error("Validation module error:", error);
		throw error;
	}
}

// Fallback validation core WITHOUT CSV blocking
// Used when ValidationRulesService fails - we already tried the proper validation path
async function validationModuleCoreWithoutCSVBlock(
	fileInput,
	validationFunction,
	config = {},
) {
	try {
		// Safety check: This function requires a validation function
		if (!validationFunction || typeof validationFunction !== "function") {
			throw new Error(
				"validationModuleCoreWithoutCSVBlock requires a validation function",
			);
		}

		// Extract file from array/FileList if needed
		let rawFile;
		if (Array.isArray(fileInput)) {
			rawFile = fileInput[0];
		} else if (fileInput instanceof FileList) {
			rawFile = fileInput[0];
		} else {
			rawFile = fileInput;
		}

		const file = normalizeFileObject(rawFile);

		// Basic file validation
		if (!file) {
			throw new Error("No file provided");
		}

		// File size validation (default 5MB)
		const maxSize = config.maxSize || 5 * 1024 * 1024;
		if (typeof file.size === "number" && file.size > maxSize) {
			throw new Error(
				`File size exceeds ${Math.ceil(maxSize / 1024 / 1024)}MB limit`,
			);
		} else if (file.size === undefined || file.size === null) {
			console.warn(
				"File size is missing in validationModule fallback, skipping size validation",
			);
		}

		// File type validation (if specified)
		if (config.accept && config.accept !== "*") {
			const acceptedTypes = config.accept.split(",").map((type) => type.trim());
			const fileName = file.name || "unknown_file";
			const fileExtension = fileName.includes(".")
				? `.${fileName.split(".").pop().toLowerCase()}`
				: "";
			const mimeType = file.type;

			const isValidExtension = acceptedTypes.some(
				(type) =>
					type === fileExtension || fileName.toLowerCase().endsWith(type),
			);
			const isValidMimeType = acceptedTypes.some(
				(type) => mimeType === type || mimeType.includes(type.replace(".", "")),
			);

			if (!isValidExtension && !isValidMimeType) {
				throw new Error(`Please select a valid file type: ${config.accept}`);
			}
		}

		// Custom validation - NO CSV BLOCKING for fallback path
		// This function is only called when ValidationRulesService already tried and failed
		if (validationFunction && typeof validationFunction === "function") {
			// Check if it's a WASM validation (ACH files)
			if (validationFunction.name === "parseACH" || config.isWASM) {
				// WASM validation for ACH files
				await window.wasmReady;
				const content = await file.text();

				if (typeof window.parseACH === "function") {
					const result = window.parseACH(content);
					if (typeof result !== "string" || result[0] !== "{") {
						throw new Error("Invalid ACH file format");
					}
					// Return parsed content for ACH files
					return { validated: true, content: result };
				} else {
					throw new Error("ACH validator not loaded");
				}
			} else {
				// Callback validation for all files (including CSV in fallback mode)
				console.log(
					"📝 Using fallback validation (ValidationRulesService unavailable)",
				);
				const isValid = await validationFunction(
					file,
					config.validationConfig || {},
				);
				if (!isValid) {
					throw new Error("File validation failed");
				}
			}
		}

		// Return validation success
		return { validated: true, content: file };
	} catch (error) {
		console.error("Fallback validation module error:", error);
		throw error;
	}
}

// Vue plugin definition
export default {
	install(app, options = {}) {
		// Add $validationModule to all component instances
		app.config.globalProperties.$validationModule = async (
			fileInput,
			validationFunction,
			config = {},
		) => {
			const result = await validationModuleCore(fileInput, validationFunction, {
				...options,
				...config,
			});
			return result;
		};

		// Enhanced validation module that can use rule-based validation when available
		app.config.globalProperties.$validateWithRulesFirst = async function (
			fileInput,
			ruleType,
			validationFunction,
			config = {},
		) {
			// Try rule-based validation first if validationRulesService is available
			if (this.$validationRulesService) {
				try {
					// Bind Vue context for API access
					this.$validationRulesService.bindVueContext(this);

					// CRITICAL: Properly extract File from FileList/Array
					const file = Array.isArray(fileInput)
						? fileInput[0]
						: fileInput instanceof FileList
							? fileInput[0]
							: fileInput;

					console.log("📤 Extracted file for validation:", {
						originalInputType: fileInput?.constructor?.name,
						extractedFileName: file?.name,
						extractedFileSize: file?.size,
						isFileInstance: file instanceof File,
					});

					const result = await this.$validationRulesService.validateWithRules(
						file,
						ruleType,
						config,
					);

					if (result.isValid) {
						return {
							validated: true,
							content: result.processedFile || file,
							processedFile: result.processedFile,
							rulesBased: true,
						};
					} else {
						// Rule validation failed, throw error with rule details
						// Format errors for better display in toast notifications
						let errorMessages;
						if (result.errors.length === 1) {
							// Single error: display as-is
							errorMessages = result.errors[0].message;
						} else {
							// Multiple errors: format with numbered list for readability
							// Limit to 100 errors to prevent UI freeze
							const MAX_ERRORS_TO_DISPLAY = 100;
							const errorsToShow = result.errors.slice(
								0,
								MAX_ERRORS_TO_DISPLAY,
							);
							const hasMoreErrors =
								result.errors.length > MAX_ERRORS_TO_DISPLAY;

							errorMessages =
								"Validation errors:\n" +
								errorsToShow
									.map((error, index) => `${index + 1}. ${error.message}`)
									.join("\n");

							if (hasMoreErrors) {
								const remainingCount =
									result.errors.length - MAX_ERRORS_TO_DISPLAY;
								errorMessages += `\n\n... and ${remainingCount} more error${remainingCount > 1 ? "s" : ""}. Please fix the errors above and re-upload to see remaining issues.`;
							}
						}
						throw new Error(errorMessages);
					}
				} catch (error) {
					// Check if this is a CSV file - NO FALLBACK for CSV files
					const file = Array.isArray(fileInput)
						? fileInput[0]
						: fileInput instanceof FileList
							? fileInput[0]
							: fileInput;
					const fileName = file?.name || "";
					const fileType = file?.type || "";
					const isCsvFile =
						fileName.toLowerCase().endsWith(".csv") ||
						fileType === "text/csv" ||
						fileType === "application/csv";

					if (isCsvFile) {
						// CSV files MUST be validated through ValidationRulesService with API
						// NO FALLBACK to callback validation for CSV files
						console.error(
							"❌ CSV validation through ValidationRulesService failed - NO FALLBACK:",
							error.message,
						);
						throw error; // Re-throw the original error from ValidationRulesService
					}

					// For non-CSV files, allow fallback if callback function exists
					if (validationFunction && typeof validationFunction === "function") {
						console.warn(
							"⚠️ ValidationRulesService failed for non-CSV file, falling back to callback validation:",
							error.message,
						);
						return await validationModuleCoreWithoutCSVBlock(
							fileInput,
							validationFunction,
							config,
						);
					} else {
						// NO fallback function - this is a real validation error, re-throw it
						console.error(
							"❌ ValidationRulesService validation failed:",
							error.message,
						);
						throw error;
					}
				}
			} else {
				// No rules service available - CRITICAL ERROR
				console.error(
					"❌ CRITICAL: ValidationRulesService not available in $validateWithRulesFirst",
				);

				// Extract file to check if it's CSV
				const file = Array.isArray(fileInput)
					? fileInput[0]
					: fileInput instanceof FileList
						? fileInput[0]
						: fileInput;

				const isCsvFile =
					file?.name?.toLowerCase().endsWith(".csv") ||
					file?.type === "text/csv" ||
					file?.type === "application/csv";

				if (isCsvFile) {
					// CSV files REQUIRE validation service - NO FALLBACK
					console.error(
						"❌ CSV file upload blocked: ValidationRulesService unavailable",
					);
					throw new Error(
						"System Error: Validation service unavailable. CSV files cannot be processed without API validation. Please refresh the page and try again, or contact support.",
					);
				}

				// For non-CSV files, allow callback fallback
				if (validationFunction && typeof validationFunction === "function") {
					console.warn(
						"⚠️ ValidationRulesService not available, using callback validation for non-CSV file",
					);
					return await validationModuleCoreWithoutCSVBlock(
						fileInput,
						validationFunction,
						config,
					);
				} else {
					throw new Error(
						"Validation failed: ValidationRulesService unavailable and no fallback validation function provided",
					);
				}
			}
		};

		// Also provide as a global property for composition API if needed
		app.provide(
			"validationModule",
			app.config.globalProperties.$validationModule,
		);
		app.provide(
			"validateWithRulesFirst",
			app.config.globalProperties.$validateWithRulesFirst,
		);
	},
};

// Export the core function for direct use if needed
export { validationModuleCore };
